import { isAlreadyLinked } from 'shared/domain/issue/linkedDocumentation';
import type { StateCreator } from 'zustand';
import {
  createExportAsVersionPayload,
  createExportPayload,
} from 'shared/domain/issue/mapping/exportDocument/toOut/createExportDocumentOutDto';
import { issueClient } from 'shared/domain/issue/issueClient';
import type { ExportToDocumentationResultData } from 'shared/domain/issue/linkedDocumentation/operations';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import {
  createInitialActionsState,
  makeAction,
  type ZustandSet,
} from 'store/types';
import type { IssueLinkedDocumentationSlice } from '../issueLinkedDocumentation/types';
import { getExportFileName } from './model';
import {
  ExportToDocumentationArgs,
  ImportFromDocumentationArgs,
  RemoveLinkedDocumentationArgs,
  IssueDocumentationOperationsSlice,
  issueDocumentationOperationNames,
} from './types';

type IssueDocumentationOperationsStore = IssueDocumentationOperationsSlice &
  Pick<IssueLinkedDocumentationSlice, 'addPendingRemoval' | 'removePendingRemoval'>;

type IssueDocumentationOperationsSliceCreator = StateCreator<
  IssueDocumentationOperationsStore,
  [],
  [],
  IssueDocumentationOperationsSlice
>;

function toOperationError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(getErrorMessage(error) || fallbackMessage);
}

export const createIssueDocumentationOperationsSlice: IssueDocumentationOperationsSliceCreator = (
  set,
  get
) => {
  const storeSet = set as ZustandSet<IssueDocumentationOperationsStore>;

  return {
    _actionStates: createInitialActionsState(issueDocumentationOperationNames),
    lastError: null,
    setLastError: (lastError) => set({ lastError }),
    exportToDocumentation: makeAction(
      storeSet,
      'exportToDocumentation',
      async (
        actionSet,
        args: ExportToDocumentationArgs
      ): Promise<ExportToDocumentationResultData> => {
        const {
          issueId,
          fileToExport,
          currentDirectoryId,
          selectedDirectoryId,
          targetDocumentationId,
          version,
        } = args;

        if (!issueId || !fileToExport._id) {
          throw new Error('Missing issueId or fileToExport._id');
        }

        actionSet({ lastError: null });

        const payload = targetDocumentationId
          ? createExportAsVersionPayload(targetDocumentationId)
          : createExportPayload({
              currentDirectoryId:
                selectedDirectoryId ?? currentDirectoryId ?? ROOT_DIR_ID,
              fileTitle: getExportFileName(fileToExport),
            });

        try {
          return await issueClient.exportToDocumentation({
            issueId,
            documentId: fileToExport._id,
            payload,
            version,
            localId: fileToExport.localId,
            imageRotation: fileToExport.imageRotation,
          });
        } catch (error) {
          const operationError = toOperationError(
            error,
            'Export to documentation failed'
          );
          actionSet({ lastError: operationError.message });
          throw operationError;
        }
      }
    ),
    importFromDocumentation: makeAction(
      storeSet,
      'importFromDocumentation',
      async (actionSet, args: ImportFromDocumentationArgs): Promise<void> => {
        const { issueId, issue, target } = args;

        if (!issueId || !target.documentationId) {
          throw new Error('Missing issueId or documentationId');
        }

        actionSet({ lastError: null });

        if (isAlreadyLinked(issue?.linkedDocumentation ?? [], target)) {
          return;
        }

        try {
          await issueClient.importFromDocumentation({ issueId, payload: target });
        } catch (error) {
          const operationError = toOperationError(
            error,
            'Import from documentation failed'
          );
          actionSet({ lastError: operationError.message });
          throw operationError;
        }
      }
    ),
    removeLinkedDocumentation: makeAction(
      storeSet,
      'removeLinkedDocumentation',
      async (actionSet, args: RemoveLinkedDocumentationArgs): Promise<void> => {
        const { issueId, linkedDocumentationId } = args;

        if (!issueId || !linkedDocumentationId) {
          throw new Error('Missing issueId or linkedDocumentationId');
        }

        get().addPendingRemoval(linkedDocumentationId);
        actionSet({ lastError: null });

        try {
          await issueClient.removeLinkedDocumentation({ issueId, linkedDocumentationId });
        } catch (error) {
          const operationError = toOperationError(
            error,
            'Remove linked documentation failed'
          );
          get().removePendingRemoval(linkedDocumentationId);
          actionSet({ lastError: operationError.message });
          throw operationError;
        }
      }
    ),
    resetIssueDocumentationOperations: () =>
      set({
        _actionStates: createInitialActionsState(
          issueDocumentationOperationNames
        ),
        lastError: null,
      }),
  };
};

export type {
  IssueDocumentationOperationsSlice,
  ExportAsVersion,
  ExportToDocumentationArgs,
  ImportFromDocumentationArgs,
  RemoveLinkedDocumentationArgs,
} from './types';
